/**
 * Tournament utility functions
 */

import { TournamentDTO } from '../types/tournaments'

/**
 * Format tournament date from ISO 8601 string to display format
 */
export const formatTournamentDate = (
    dateString: string
): { date: string; time: string } => {
    try {
        const date = new Date(dateString)

        // Format date as "25 April"
        const dateStr = date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'long',
        })

        // Format time as "14:00"
        const hours = date.getHours()
        const minutes = date.getMinutes()
        const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`

        return { date: dateStr, time: timeStr }
    } catch {
        return {
            date: dateString,
            time: '',
        }
    }
}

/**
 * Format tournament date range
 */
export const formatTournamentDateRange = (
    startDate: string,
    endDate: string
): string => {
    try {
        const start = new Date(startDate)
        const end = new Date(endDate)

        const startDateStr = start.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'long',
        })

        const startTime = `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')}`
        const endTime = `${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}`

        return `${startDateStr} – ${startTime} – ${endTime}`
    } catch {
        return `${startDate} – ${endDate}`
    }
}

/**
 * Format date as DD.MM.YYYY
 */
export const formatDateDDMMYYYY = (dateString: string): string => {
    try {
        const date = new Date(dateString)
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        return `${day}.${month}.${year}`
    } catch {
        return dateString
    }
}

/**
 * Format price from cents to display format
 */
export const formatPrice = (
    cents: number,
    currency: string = 'EUR'
): string => {
    const amount = cents / 100
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount)
}

/**
 * Check if tournament is upcoming (status is 'upcoming' and date is in the future)
 */
export const isUpcomingTournament = (tournament: TournamentDTO): boolean => {
    if (tournament.status === 'past' || tournament.status === 'cancelled') {
        return false
    }

    const now = new Date()
    const tournamentDate = new Date(tournament.info.startDate)
    return tournamentDate >= now
}

/**
 * Check if tournament is past (status is 'past' or date is in the past)
 */
export const isPastTournament = (tournament: TournamentDTO): boolean => {
    if (tournament.status === 'past') {
        return true
    }

    const now = new Date()
    const tournamentDate = new Date(tournament.info.startDate)
    return tournamentDate < now && tournament.status !== 'cancelled'
}
